// MACHINERY V2 (Stroje) — affected-part options + the offline fallback (M2.6).
//
// Spec: data package §7, row `affectedPartCategoryId`. Reference impl:
// components/CreateServiceReportPage.tsx — `MACHINE_PART_GROUPS` (:69-127) and
// the `partOptions` useMemo (:288-311).
//
// THE FALLBACK IS AN INTENTIONAL DUPLICATE, exactly like M2.9's
// `V2_DOC_TYPE_KEYS`. v1 declares `MACHINE_PART_GROUPS` as a module-private
// `const` (not exported), and its file imports MUI at the top — importing from
// it would pull MUI into the v2 bundle graph and break the §3 stack rule. So
// the 15 groups are copied verbatim, English names included.
//
// THE FALLBACK IS NOT SELECTABLE, AND THAT IS THE v1 CONTRACT. Its entries
// carry no `categoryId`, and `affectedPartCategoryId` is a Guid FK — v1 filters
// them out of the list (`options.filter((o) => !!o.categoryId)`,
// CreateServiceReportPage.tsx:698), leaving an EMPTY dropdown until
// `GET /v1/machine-parts` resolves. v2 keeps them unselectable but RENDERS them
// disabled, so the user sees the shape of the list instead of an empty panel.
// Same non-selectability, better empty state. Never send a fallback entry.
export interface SrPartOption {
  /** `part.id` from the API = the `affectedPartCategoryId` value. Empty string
   *  for a fallback row, which is why those rows are rendered disabled. */
  id: string;
  label: string;
  group: string;
  /** The API's English `part.name`, carried for parity (v1 keeps it on the
   *  option as `englishName`). Not rendered today. */
  englishName: string;
  selectable: boolean;
}

/** Copied verbatim from CreateServiceReportPage.tsx:69-127. */
export const MACHINE_PART_GROUPS: { groupKey: string; parts: { key: string; en: string }[] }[] = [
  { groupKey: "spindleSystem", parts: [
    { key: "mainSpindle", en: "Main spindle" }, { key: "spindleMotor", en: "Spindle motor" },
    { key: "spindleBearings", en: "Spindle bearings" }, { key: "toolTaperDrawbar", en: "Tool taper / drawbar" },
  ] },
  { groupKey: "axisDrives", parts: [
    { key: "servoMotors", en: "Servo motors" }, { key: "ballScrews", en: "Ball screws" },
    { key: "linearGuidesRails", en: "Linear guides / rails" }, { key: "axisBearings", en: "Axis bearings" },
  ] },
  { groupKey: "cncControl", parts: [
    { key: "controllerHmi", en: "Controller / HMI" }, { key: "ioBoards", en: "I/O boards" },
    { key: "plcModule", en: "PLC module" }, { key: "softwareFirmware", en: "Software / firmware" },
  ] },
  { groupKey: "electrical", parts: [
    { key: "powerSupply", en: "Power supply" }, { key: "cablesWiring", en: "Cables / wiring" },
    { key: "sensorsSwitches", en: "Sensors / switches" }, { key: "contactorsRelays", en: "Contactors / relays" },
  ] },
  { groupKey: "hydraulicSystem", parts: [
    { key: "hydraulicPump", en: "Hydraulic pump" }, { key: "cylinders", en: "Cylinders" },
    { key: "valves", en: "Valves" }, { key: "hosesFittings", en: "Hoses / fittings" },
  ] },
  { groupKey: "pneumaticSystem", parts: [
    { key: "airSystem", en: "Air system" }, { key: "pneumaticValves", en: "Pneumatic valves" },
    { key: "pneumaticCylinders", en: "Pneumatic cylinders" },
  ] },
  { groupKey: "coolantSystem", parts: [
    { key: "coolantPump", en: "Coolant pump" }, { key: "nozzles", en: "Nozzles" },
    { key: "tankFiltration", en: "Tank / filtration" },
  ] },
  { groupKey: "lubrication", parts: [
    { key: "lubricationPump", en: "Lubrication pump" }, { key: "distributionLines", en: "Distribution lines" },
  ] },
  { groupKey: "toolChanger", parts: [
    { key: "atcMechanism", en: "ATC mechanism" }, { key: "toolMagazine", en: "Tool magazine" },
    { key: "gripperArm", en: "Gripper arm" },
  ] },
  { groupKey: "turretLathes", parts: [
    { key: "turretMechanism", en: "Turret mechanism" }, { key: "toolHolders", en: "Tool holders" },
    { key: "indexingMotor", en: "Indexing motor" },
  ] },
  { groupKey: "workholding", parts: [
    { key: "chuckCollet", en: "Chuck / collet" }, { key: "jaws", en: "Jaws" },
    { key: "tailstock", en: "Tailstock" }, { key: "steadyRest", en: "Steady rest" },
  ] },
  { groupKey: "chipManagement", parts: [
    { key: "chipConveyor", en: "Chip conveyor" }, { key: "chipAuger", en: "Chip auger" },
  ] },
  { groupKey: "enclosureSafety", parts: [
    { key: "doorsGuards", en: "Doors / guards" }, { key: "interlocks", en: "Interlocks" },
    { key: "wayCovers", en: "Way covers" },
  ] },
  { groupKey: "consumables", parts: [
    { key: "filters", en: "Filters" }, { key: "belts", en: "Belts" },
    { key: "sealsORings", en: "Seals / O-rings" }, { key: "wipers", en: "Wipers" },
  ] },
  { groupKey: "other", parts: [{ key: "other", en: "Other" }] },
];

/** `main_spindle` -> `mainSpindle`, the v1 translation-key derivation (:295). */
export const partTranslationKey = (code: string): string =>
  code.replace(/_([a-z])/g, (_m: string, c: string) => c.toUpperCase());

export interface SrPartGroup {
  groupKey: string;
  parts: { id: string; name: string; code: string }[];
}

/**
 * The dropdown's options. `machineParts` is the `GET /v1/machine-parts`
 * payload; when it is empty the fallback takes over, unselectable.
 * `translate` is the caller's i18n function (namespace `servicing`).
 */
export const buildPartOptions = (
  machineParts: SrPartGroup[] | undefined,
  translate: (key: string) => string
): SrPartOption[] => {
  const options: SrPartOption[] = [];
  if (machineParts && machineParts.length > 0) {
    machineParts.forEach((group) => {
      const groupLabel = translate(`servicing:machineParts.groups.${group.groupKey}`);
      (group.parts || []).forEach((part) => {
        options.push({
          id: part.id,
          label: translate(`servicing:machineParts.parts.${partTranslationKey(part.code)}`),
          group: groupLabel,
          englishName: part.name,
          selectable: true,
        });
      });
    });
    return options;
  }
  MACHINE_PART_GROUPS.forEach((group) => {
    const groupLabel = translate(`servicing:machineParts.groups.${group.groupKey}`);
    group.parts.forEach((part) => {
      options.push({
        id: "",
        label: translate(`servicing:machineParts.parts.${part.key}`),
        group: groupLabel,
        englishName: part.en,
        selectable: false,
      });
    });
  });
  return options;
};
